import React, { useState } from 'react';
import axios from 'axios';
import { Search, X, Users, TrendingUp, Calendar, Clock, Printer } from 'lucide-react';
import { TextField, Button } from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/pt-br';
import Lottie from 'lottie-react';
import './App.css';

dayjs.locale('pt-br');

interface RelatorioData {
  data: string;
  encontrado: boolean;
  registros: any[];
  fonte: string;
}

interface RelatorioResponse {
  sucesso: boolean;
  matricula: string;
  nomeFuncionario: string;
  periodo: { inicio: string; fim: string };
  totalDias: number;
  diasComDados: number;
  totalViagens: number;
  dados: RelatorioData[];
}

function App() {
  const [matricula, setMatricula] = useState<string>('');
  const [dataInicio, setDataInicio] = useState<Dayjs | null>(dayjs());
  const [dataFim, setDataFim] = useState<Dayjs | null>(dayjs());
  const [loading, setLoading] = useState<boolean>(false);
  const [relatorio, setRelatorio] = useState<RelatorioResponse | null>(null);
  const [erro, setErro] = useState<string>('');
  const [expandedSecao, setExpandedSecao] = useState<string | null>(null);
  const [iconData, setIconData] = useState<any>(null);

  React.useEffect(() => {
    fetch('/icon-titulo.json')
      .then(res => res.json())
      .then(data => setIconData(data))
      .catch(err => console.error('Erro ao carregar ícone:', err));
  }, []);

  const buscarRelatorio = async () => {
    setLoading(true);
    setErro('');
    
    if (!dataInicio || !dataFim) {
      setErro('Por favor, selecione as datas inicial e final');
      setLoading(false);
      return;
    }
    
    try {
      const response = await axios.post('/api/relatorio', {
        matricula,
        dataInicio: dataInicio.format('YYYY-MM-DD'),
        dataFim: dataFim.format('YYYY-MM-DD')
      });
      
      // Deduplicar seções antes de exibir
      const dadosDedupicados = deduplicarSecoes(response.data.dados);
      
      // Recalcular totalViagens com dados dedupicados
      const totalViagensDedupicado = dadosDedupicados.reduce((acc: number, d: RelatorioData) => 
        acc + d.registros.reduce((sum: number, r: any) => sum + (r.viagens?.length || 0), 0), 0
      );
      
      setRelatorio({
        ...response.data,
        dados: dadosDedupicados,
        totalViagens: totalViagensDedupicado
      });
    } catch (error: any) {
      setErro(error.response?.data?.erro || 'Erro ao buscar dados');
    } finally {
      setLoading(false);
    }
  };

  const limparFiltros = () => {
    setMatricula('');
    setDataInicio(dayjs());
    setDataFim(dayjs());
    setRelatorio(null);
    setErro('');
    setExpandedSecao(null);
  };

  // Função para deduplicar seções
  const deduplicarSecoes = (dados: RelatorioData[]) => {
    return dados.map(dia => {
      const secoesDuplicadas = new Map<string, any>();
      
      dia.registros.forEach((secao: any) => {
        const chave = `${secao.ini}-${secao.fim}-${secao.cobrador}`;
        if (!secoesDuplicadas.has(chave)) {
          secoesDuplicadas.set(chave, secao);
        }
      });
      
      return {
        ...dia,
        registros: Array.from(secoesDuplicadas.values())
      };
    });
  };

  // Função para imprimir relatório
  const imprimirRelatorio = () => {
    if (!relatorio) return;

    const svgCheck = '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#166534" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:middle;margin-right:3px"><polyline points="20 6 9 17 4 12"></polyline></svg>';
    const svgWrench = '<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#991b1b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:middle;margin-right:3px"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path></svg>';

    // Gerar linhas da tabela
    const linhasTabela = relatorio.dados.map((dia: any) => {
      const linhasUnicas = [...new Set(dia.registros.flatMap((r: any) => (r.viagens || []).map((v: any) => v.linha)))].join(', ');
      const veiculosUnicos = [...new Set(dia.registros.flatMap((r: any) => (r.viagens || []).map((v: any) => v.veiculo)))].join(', ');
      const totalViagens = dia.registros.reduce((sum: number, r: any) => sum + (r.viagens?.length || 0), 0);
      
      return '<tr><td>' + dayjs(dia.data, 'DD/MM/YYYY').format('DD/MM/YYYY') + '</td><td>' + dia.registros.length + '</td><td>' + totalViagens + '</td><td>' + (linhasUnicas || 'N/A') + '</td><td>' + (veiculosUnicos || 'N/A') + '</td></tr>';
    }).join('');

    // Gerar detalhes de viagens
    let detalhesViagens = '';
    relatorio.dados.forEach((dia: any) => {
      dia.registros.forEach((secao: any) => {
        detalhesViagens += '<div class="secao-block">';
        detalhesViagens += '<div class="secao-header">Data: ' + dia.data + '  |  Horario: ' + secao.ini + ' ate ' + secao.fim + '</div>';
        detalhesViagens += '<div class="secao-body"><span class="viagens-count">Viagens (' + (secao.viagens?.length || 0) + ')</span><div class="viagens-list">';
        
        (secao.viagens || []).forEach((v: any, idx: number) => {
          const tipoClass = v.tipoViagem === 'Viagem Editada' ? 'tipo-editada' : 'tipo-normal';
          const tipoIcon = v.tipoViagem === 'Viagem Editada' ? svgWrench : svgCheck;
          const tipoText = v.tipoViagem === 'Viagem Editada' ? 'Editada' : 'Normal';
          detalhesViagens += '<div class="viagem-item"><span class="viagem-num">' + (idx + 1) + '.</span><span class="viagem-horario">' + v.ini + ' &rarr; ' + v.fim + '</span><span class="viagem-info">Linha: ' + v.linha + '</span><span class="viagem-info">Veiculo: ' + v.veiculo + '</span><span class="viagem-tipo ' + tipoClass + '">' + tipoIcon + tipoText + '</span></div>';
        });
        
        detalhesViagens += '</div></div></div>';
      });
    });

    const conteudo = `<!DOCTYPE html><html lang="pt-BR"><head><meta charset="UTF-8"><title>Relatorio Operador - ${relatorio.nomeFuncionario}</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Segoe UI', Arial, sans-serif; line-height: 1.5; color: #1e293b; font-size: 12px; }
  @page { size: A4; margin: 12mm 15mm; }
  .container { width: 100%; max-width: 210mm; margin: 0 auto; }

  /* Header com logo */
  .header { display: flex; align-items: center; gap: 20px; border-bottom: 2px solid #2056DF; padding-bottom: 12px; margin-bottom: 16px; }
  .header-logo { height: 55px; width: auto; }
  .header-text { flex: 1; }
  .header-text h1 { color: #2056DF; font-size: 20px; font-weight: 700; letter-spacing: 0.5px; }
  .header-text p { color: #64748b; font-size: 11px; font-weight: 500; }
  .header-date { text-align: right; font-size: 10px; color: #94a3b8; }

  /* Info section */
  .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 6px 24px; margin-bottom: 16px; padding: 12px; background: #f8fafc; border-radius: 6px; border: 1px solid #e2e8f0; }
  .info-row { display: flex; align-items: center; gap: 8px; }
  .info-label { font-weight: 600; color: #475569; min-width: 120px; font-size: 11px; }
  .info-value { color: #1e293b; font-weight: 500; }

  /* Table */
  table { width: 100%; border-collapse: collapse; margin: 12px 0; font-size: 11px; }
  thead th { background-color: #2056DF; color: white; padding: 8px 10px; text-align: left; font-weight: 600; font-size: 11px; }
  td { padding: 7px 10px; border-bottom: 1px solid #e2e8f0; }
  tbody tr:nth-child(even) { background-color: #f8fafc; }

  /* Section details */
  .section-title { color: #2056DF; font-size: 14px; font-weight: 700; margin: 18px 0 10px; padding-bottom: 6px; border-bottom: 1px solid #e2e8f0; }
  .secao-block { margin-bottom: 14px; border: 1px solid #e2e8f0; border-radius: 6px; overflow: hidden; page-break-inside: avoid; }
  .secao-header { background: #2056DF; color: white; padding: 8px 14px; font-weight: 600; font-size: 12px; }
  .secao-body { padding: 10px 14px; }
  .viagens-count { font-weight: 700; font-size: 11px; color: #475569; display: block; margin-bottom: 6px; }
  .viagens-list { }
  .viagem-item { display: flex; align-items: center; gap: 8px; padding: 4px 0; border-bottom: 1px solid #f1f5f9; font-size: 11px; }
  .viagem-item:last-child { border-bottom: none; }
  .viagem-num { font-weight: 700; color: #64748b; min-width: 22px; }
  .viagem-horario { font-weight: 600; font-family: 'Consolas', 'Courier New', monospace; }
  .viagem-info { color: #64748b; }
  .viagem-tipo { padding: 1px 8px; border-radius: 10px; font-size: 10px; font-weight: 600; white-space: nowrap; display: inline-flex; align-items: center; }
  .tipo-editada { background-color: #fef2f2; color: #991b1b; border: 1px solid #fecaca; }
  .tipo-normal { background-color: #f0fdf4; color: #166534; border: 1px solid #bbf7d0; }

  /* Footer */
  .footer { text-align: center; font-size: 9px; color: #94a3b8; margin-top: 24px; padding-top: 8px; border-top: 1px solid #e2e8f0; }
  @media print { body { margin: 0; padding: 0; } .secao-block { page-break-inside: avoid; } }
</style></head><body>
<div class="container">
  <div class="header">
    <img src="/logo.png" class="header-logo" alt="Logo" />
    <div class="header-text">
      <h1>RELATORIO HISTORICO OPERACIONAL</h1>
      <p>Viacao Sao Pedro - Connectbus</p>
    </div>
    <div class="header-date">Emitido em<br/><strong>${dayjs().format('DD/MM/YYYY HH:mm')}</strong></div>
  </div>

  <div class="info-grid">
    <div class="info-row"><span class="info-label">Operador:</span><span class="info-value">${relatorio.nomeFuncionario} (${relatorio.matricula})</span></div>
    <div class="info-row"><span class="info-label">Periodo:</span><span class="info-value">${dayjs(relatorio.periodo.inicio, 'YYYY-MM-DD').format('DD/MM/YYYY')} ate ${dayjs(relatorio.periodo.fim, 'YYYY-MM-DD').format('DD/MM/YYYY')}</span></div>
    <div class="info-row"><span class="info-label">Total de Dias:</span><span class="info-value">${relatorio.totalDias}</span></div>
    <div class="info-row"><span class="info-label">Dias com Dados:</span><span class="info-value">${relatorio.diasComDados}</span></div>
    <div class="info-row"><span class="info-label">Total de Viagens:</span><span class="info-value">${relatorio.totalViagens}</span></div>
  </div>

  <table><thead><tr><th>Data</th><th>Secoes</th><th>Viagens</th><th>Linhas</th><th>Veiculos</th></tr></thead><tbody>${linhasTabela}</tbody></table>

  <div class="section-title">Detalhes de Viagens por Secao</div>
  ${detalhesViagens}

  <div class="footer">Documento gerado automaticamente pelo sistema Connectbus &mdash; Viacao Sao Pedro</div>
</div></body></html>`;

    const janela = window.open('', '', 'height=700,width=900');
    if (janela) {
      janela.document.write(conteudo);
      janela.document.close();
      setTimeout(() => janela.print(), 300);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-400 via-blue-400 to-white-400 flex flex-col relative overflow-hidden">
      {/* Elementos decorativos de fundo */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-purple-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '2s' }}></div>
        <div className="absolute bottom-0 left-1/2 w-96 h-96 bg-pink-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '4s' }}></div>
      </div>

      {/* Conteúdo */}
      <div className="relative z-10 flex flex-col flex-1">
      {/* Header */}
      <header className="bg-white bg-opacity-95 border-b border-slate-200 sticky top-0 z-50 shadow-lg relative">
        <div className="w-full px-4 sm:px-6 py-4 flex flex-col sm:flex-row items-center justify-center gap-4">
          <img src="/logo.png" alt="Connectbus Logo" className="absolute left-4 sm:left-6 h-16 w-auto" />
          <div className="text-center">
            <h1 className="text-2xl sm:text-3xl font-bold" style={{ color: '#2056DF' }}>PAINEL DE HISTÓRICO OPERACIONAL</h1>
            <p className="text-sm sm:text-base font-medium" style={{ color: '#2056DF' }}>Relatório de Operadores</p>
          </div>
        </div>
      </header>

      <main className="flex-1 w-full px-4 sm:px-6 py-6 sm:py-8 overflow-auto">
        {/* Filtros */}
        <div className="bg-white rounded-lg border border-slate-200 p-4 sm:p-6 mb-6 shadow-sm">
          <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4 justify-between">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-end flex-1">
              <h2 className="text-lg font-semibold" style={{ color: '#2056DF' }}>Filtros de Busca</h2>
              
              <TextField
                label="Matrícula do Operador"
                value={matricula}
                onChange={(e) => setMatricula(e.target.value)}
                placeholder="Ex: 3356"
                size="small"
                variant="outlined"
                style={{ minWidth: '150px' }}
              />
              
              <div className="flex gap-3 sm:gap-4">
                <div className="flex items-end gap-2">
                  <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                    <DatePicker
                      label="Início"
                      value={dataInicio}
                      onChange={(newValue: Dayjs | null) => setDataInicio(newValue)}
                      slotProps={{
                        textField: { size: 'small', fullWidth: true }
                      }}
                    />
                  </LocalizationProvider>
                </div>

                <div className="flex items-end gap-2">
                  <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                    <DatePicker
                      label="Fim"
                      value={dataFim}
                      onChange={(newValue: Dayjs | null) => setDataFim(newValue)}
                      slotProps={{
                        textField: { size: 'small', fullWidth: true }
                      }}
                    />
                  </LocalizationProvider>
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                onClick={buscarRelatorio}
                disabled={loading}
                variant="contained"
                sx={{ 
                  bgcolor: '#14b8a6', 
                  '&:hover': { bgcolor: '#0d9488' },
                  borderRadius: '8px',
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  fontSize: '14px',
                  padding: '8px 20px'
                }}
                size="small"
              >
                <Search className="w-4 h-4 mr-2" />
                Aplicar Filtro
              </Button>
              
              <Button
                onClick={limparFiltros}
                variant="contained"
                sx={{ 
                  bgcolor: '#ef4444',
                  '&:hover': { bgcolor: '#dc2626' },
                  borderRadius: '8px',
                  textTransform: 'uppercase',
                  fontWeight: 600,
                  fontSize: '14px',
                  padding: '8px 20px'
                }}
                size="small"
              >
                <X className="w-4 h-4 mr-2" />
                Limpar
              </Button>
            </div>
          </div>
        </div>

        {erro && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6 text-red-700 text-sm">
            {erro}
          </div>
        )}

        {relatorio && (
          <>
            {/* Cards de Resumo - Estilo Indicadores RH */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 sm:p-6 shadow-sm border border-blue-200 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-sm font-semibold text-blue-900">Total de Viagens</h3>
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                </div>
                <p className="text-3xl sm:text-4xl font-bold text-blue-900">{relatorio.totalViagens}</p>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 sm:p-6 shadow-sm border border-green-200 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-sm font-semibold text-green-900">Dias com Dados</h3>
                  <Calendar className="w-5 h-5 text-green-600" />
                </div>
                <p className="text-3xl sm:text-4xl font-bold text-green-900">{relatorio.diasComDados}</p>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 sm:p-6 shadow-sm border border-purple-200 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-sm font-semibold text-purple-900">Operador</h3>
                  <Users className="w-5 h-5 text-purple-600" />
                </div>
                <p className="text-3xl sm:text-4xl font-bold text-purple-900">{relatorio.matricula}</p>
                <p className="text-lg sm:text-xl font-semibold text-purple-700 mt-2">{relatorio.nomeFuncionario}</p>
              </div>

              <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4 sm:p-6 shadow-sm border border-orange-200 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <h3 className="text-sm font-semibold text-orange-900">Período</h3>
                  <Clock className="w-5 h-5 text-orange-600" />
                </div>
                <p className="text-2xl sm:text-3xl font-bold text-orange-900">
                  {dayjs(relatorio.periodo.inicio, 'YYYY-MM-DD').format('DD/MM/YYYY')} — {dayjs(relatorio.periodo.fim, 'YYYY-MM-DD').format('DD/MM/YYYY')}
                </p>
              </div>
            </div>

            {/* Tabela Responsiva */}
            <div className="bg-white bg-opacity-90 rounded-xl border border-slate-200 shadow-sm overflow-hidden mb-6">
              <div className="px-4 sm:px-6 py-4 border-b border-slate-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <h2 className="text-lg font-semibold" style={{ color: '#2056DF' }}>Detalhes por Data</h2>
                <button onClick={imprimirRelatorio} className="text-slate-600 hover:text-slate-900 flex items-center gap-2 text-sm transition-colors">
                  <Printer className="w-4 h-4" />
                  Imprimir
                </button>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-4 sm:px-6 py-3 text-center font-semibold text-slate-900">Data</th>
                      <th className="px-4 sm:px-6 py-3 text-center font-semibold text-slate-900">Status</th>
                      <th className="px-4 sm:px-6 py-3 text-center font-semibold text-slate-900">Seções</th>
                      <th className="px-4 sm:px-6 py-3 text-center font-semibold text-slate-900">Viagens</th>
                      <th className="px-4 sm:px-6 py-3 text-center font-semibold text-slate-900">Fonte</th>
                    </tr>
                  </thead>
                  <tbody>
                    {relatorio.dados.map((item, idx) => (
                      <tr key={idx} className="border-b border-slate-200 hover:bg-slate-50 transition-colors">
                        <td className="px-4 sm:px-6 py-4 font-medium text-slate-900 text-center text-base">{item.data}</td>
                        <td className="px-4 sm:px-6 py-4 text-center">
                          {item.encontrado ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-green-50 text-green-700 border border-green-200">
                              ✓ Encontrado
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium bg-slate-100 text-slate-600 border border-slate-200">
                              - Sem dados
                            </span>
                          )}
                        </td>
                        <td className="px-4 sm:px-6 py-4 text-slate-600 text-center text-base font-medium">{item.registros.length}</td>
                        <td className="px-4 sm:px-6 py-4 text-slate-600 text-center text-base font-medium">
                          {item.registros.reduce((sum, r) => sum + (r.viagens?.length || 0), 0)}
                        </td>
                        <td className="px-4 sm:px-6 py-4 text-slate-500 text-base text-center font-medium">
                          {item.fonte === 'API' ? 'SINETRAM' : item.fonte}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Seções em Cards - Layout Moderno */}
            <div className="mb-6">
              <div className="space-y-3">
                {relatorio.dados.filter(d => d.encontrado).map((dia, dayIdx) => (
                  dia.registros.map((secao, secaoIdx) => {
                    const secaoId = `${dayIdx}-${secaoIdx}`;
                    const isExpanded = expandedSecao === secaoId;
                    const linhasUnicas = [...new Set(secao.viagens?.map((v: any) => v.linha) || [])].join(', ');
                    const veiculosUnicos = [...new Set(secao.viagens?.map((v: any) => v.veiculo) || [])].join(', ');
                    
                    return (
                      <div 
                        key={secaoId}
                        className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden"
                      >
                        {/* Header Moderno */}
                        <div 
                          onClick={() => setExpandedSecao(isExpanded ? null : secaoId)}
                          className="px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 transition-all cursor-pointer"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4 flex-1">
                              <div>
                                <p className="text-blue-100 text-xs font-semibold mb-1">Data</p>
                                <p className="text-white font-bold text-lg">{dia.data}</p>
                              </div>
                              <div className="border-l-2 border-blue-400 pl-4">
                                <p className="text-blue-100 text-xs font-semibold mb-1">Horário</p>
                                <p className="text-white font-mono font-bold">{secao.ini} até {secao.fim}</p>
                              </div>
                              <div className="ml-auto flex gap-2">
                                <div className="bg-blue-500 bg-opacity-50 px-4 py-2 rounded-lg text-center">
                                  <p className="text-blue-100 text-xs font-semibold">Viagens</p>
                                  <p className="text-white font-bold text-xl">{secao.viagens?.length || 0}</p>
                                </div>
                              </div>
                            </div>
                            <svg 
                              className={`w-6 h-6 text-white transition-transform ml-4 ${isExpanded ? 'rotate-180' : ''}`}
                              fill="none" 
                              stroke="currentColor" 
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                            </svg>
                          </div>
                        </div>

                        {/* Conteúdo Expandido - Moderno */}
                        {isExpanded && (
                          <div className="p-6 space-y-6">
                            {/* Grid de Informações */}
                            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                              <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg p-4 border border-slate-200 text-center">
                                <p className="text-sm text-slate-600 font-bold uppercase mb-2">Abertura</p>
                                <p className="text-2xl font-bold text-slate-900 font-mono">{secao.ini}</p>
                              </div>
                              <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4 border border-orange-200 text-center">
                                <p className="text-sm text-orange-600 font-bold uppercase mb-2">Fechamento</p>
                                <p className="text-2xl font-bold text-orange-900 font-mono">{secao.fim}</p>
                              </div>
                              <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg p-4 border border-purple-200 text-center">
                                <p className="text-sm text-purple-600 font-bold uppercase mb-2">Cobrador</p>
                                <p className="text-2xl font-bold text-purple-900 font-mono">{String(secao.cobrador).replace(/^0+/, '') || '0'}</p>
                              </div>
                              <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg p-4 border border-blue-200 text-center">
                                <p className="text-sm text-blue-600 font-bold uppercase mb-2">Viagens</p>
                                <p className="text-2xl font-bold text-blue-900">{secao.viagens?.length || 0}</p>
                              </div>
                              <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-lg p-4 border border-green-200 text-center">
                                <p className="text-sm text-green-600 font-bold uppercase mb-2">Linhas</p>
                                <p className="text-2xl font-bold text-green-900 font-mono">{linhasUnicas || 'N/A'}</p>
                              </div>
                              <div className="bg-gradient-to-br from-pink-50 to-pink-100 rounded-lg p-4 border border-pink-200 text-center">
                                <p className="text-sm text-pink-600 font-bold uppercase mb-2">Veículos</p>
                                <p className="text-2xl font-bold text-pink-900 font-mono">{veiculosUnicos || 'N/A'}</p>
                              </div>
                            </div>

                            {/* Divisor */}
                            <div className="border-t-2 border-slate-200"></div>

                            {/* Lista de Viagens Limpa */}
                            <div>
                              <p className="text-sm font-bold text-slate-900 mb-3 uppercase tracking-wide">Viagens ({secao.viagens?.length || 0})</p>
                              <div className="space-y-1 max-h-96 overflow-y-auto">
                                {secao.viagens && secao.viagens.length > 0 ? (
                                  secao.viagens.map((viagem: any, idx: number) => {
                                    const isEditada = viagem.tipoViagem === 'Viagem Editada';
                                    return (
                                      <div key={idx} className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors text-sm font-mono group ${isEditada ? 'bg-orange-50 hover:bg-orange-100' : 'bg-green-50 hover:bg-green-100'}`}>
                                        <div className={`font-bold px-2 py-1 rounded min-w-fit ${isEditada ? 'text-orange-600 bg-orange-100' : 'text-green-600 bg-green-100'}`}>
                                          {idx + 1}
                                        </div>
                                        <div className="flex-1 flex items-center gap-4 text-slate-900">
                                          <div>
                                            <span className="font-bold">{viagem.ini}</span>
                                            <span className="text-slate-400 mx-2">→</span>
                                            <span className="font-bold">{viagem.fim}</span>
                                          </div>
                                          <span className="text-slate-600">|</span>
                                          <span className="text-slate-600">Linha: <span className="font-bold text-slate-900">{viagem.linha}</span></span>
                                          <span className="text-slate-600">({viagem.sentido || 'N/A'})</span>
                                          <span className="text-slate-600">|</span>
                                          <span className="text-slate-600">Veículo: <span className="font-bold text-slate-900">{viagem.veiculo}</span></span>
                                        </div>
                                        <div className={`ml-auto px-3 py-1 rounded-full text-xs font-bold whitespace-nowrap flex items-center gap-1 ${isEditada ? 'bg-orange-200 text-orange-800' : 'bg-green-200 text-green-800'}`}>
                                          {isEditada ? (<><svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"></path></svg> Editada</>) : (<><svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg> Normal</>)}
                                        </div>
                                      </div>
                                    );
                                  })
                                ) : (
                                  <p className="text-slate-500 text-sm py-4 text-center">Sem viagens registradas</p>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                ))}
              </div>
            </div>
          </>
        )}

        {!relatorio && !loading && !erro && (
          <div className="text-center py-12 sm:py-20">
            <div className="flex justify-center mb-4" style={{ width: 140, height: 140, margin: '0 auto' }}>
              {iconData && <Lottie animationData={iconData} loop={true} style={{ width: 140, height: 140 }} />}
            </div>
            <p className="text-white text-lg opacity-80">Selecione os filtros e clique em "Buscar" para visualizar os dados</p>
          </div>
        )}
      </main>
    </div>
    </div>
  );
}

export default App;
